import React from 'react';
import { Colors, Metrics } from '../../themes';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import Table from '@material-ui/core/Table';
import TableHeader from '../common/TableHeader';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import { withSize } from 'react-sizeme';
import { ButtonBase } from '@material-ui/core';
import Pagination from 'material-ui-flat-pagination';
import IconButton from '@material-ui/core/IconButton';
import ClearIcon from '@material-ui/icons/Clear';
import AddIcon from '@material-ui/icons/Add';
import Checkbox from '@material-ui/core/Checkbox';
import InputBase from '@material-ui/core/InputBase';
import SortIcon from '../../icons/Sort';
import TableIconWarning from '../../images/table_icon_warning.svg';
import TableIconError from '../../images/table_icon_error.svg';
// time picker
import TimePicker from 'rc-time-picker';
import '../common/styles/rc-time-picker.custom.css';
import moment from 'moment';
// date picker
import DatePicker from 'react-datepicker';
import i18n from '../../i18n';
import '../common/styles/react-datepicker.custom.css';
import { registerLocale } from 'react-datepicker';
import fi from 'date-fns/locale/fi';
import pl from 'date-fns/locale/pl';
import de from 'date-fns/locale/de';
import sv from 'date-fns/locale/sv';
import es from 'date-fns/locale/es';
registerLocale('fi', fi);
registerLocale('pl', pl);
registerLocale('de', de);
registerLocale('sv', sv);
registerLocale('es', es);

const useStyles = makeStyles((theme) => ({
  root: {
    minWidth: 280,
    width: '100%',
  },
  headerContainer: {
    minHeight: 60,
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    borderRadius: '4px 4px 0 0',
    padding: theme.spacing(2),
    [theme.breakpoints.down(Metrics.minWidth)]: {
      padding: theme.spacing(2),
      justifyContent: 'flex-start',
    },
    [theme.breakpoints.between('sm', 'lg')]: {
      padding: theme.spacing(2),
      minHeight: 60,
    },
  },
  tableWrapper: {
    marginBottom: 10,
    paddingBottom: 10,
    overflowX: 'auto',
    '&::-webkit-scrollbar': {
      height: '0.8em',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 10,
      backgroundColor: Colors.navBackgroundGrey,
    },
  },
  table: {
    minWidth: 280,
  },
  emptyHeaderCell: {
    width: 42,
  },
  activeRow: {
    '& .MuiTableCell-body, & .MuiInputBase-input': {
      color: Colors.blue,
    },
    '&:nth-child(n)': {
      backgroundColor: Colors.white,
    },
  },
  warning: {
    '& .MuiTableCell-body, & .MuiInputBase-input': {
      color: Colors.white,
    },
    '&:nth-child(n)': {
      backgroundColor: Colors.warningYellow,
    },
  },
  error: {
    '& .MuiTableCell-body, & .MuiInputBase-input': {
      color: Colors.white,
    },
    '&:nth-child(n)': {
      backgroundColor: Colors.red,
    },
  },
  lightTheme: {
    '& .MuiTableSortLabel-root': {
      color: Colors.disabledGrey,
    },
    '& .MuiTableSortLabel-root.MuiTableSortLabel-active': {
      color: Colors.orange,
    },
    '& .MuiTableCell-body': {
      color: Colors.black,
    },
    '& .MuiButtonBase-root.Mui-disabled': {
      cursor: 'default',
    },
    '& .MuiButtonBase-root.MuiFlatPageButton-root.Mui-disabled': {
      color: Colors.lightDisabledGrey,
      cursor: 'default',
    },
    '& .MuiTableRow-root:nth-child(odd)': {
      background: Colors.white,
    },
    '& .MuiTableRow-root:nth-child(even)': {
      background: Colors.lightHoverGrey,
    },
    '& .MuiTableRow-root:hover': {
      background: Colors.white,
    },
    '& .MuiCheckbox-root': {
      color: Colors.disabledGrey,
    },
    '& .MuiCheckbox-root.Mui-checked': {
      color: Colors.blue,
    },
  },
  defaultCursor: {
    cursor: 'default',
  },
  deleteCell: {
    textAlign: 'right',
    paddingRight: 14,
  },
  tableCellButton: {
    padding: '11px 20px',
    width: '100%',
    fontSize: 16,
  },
  tableBodyCellButton: {
    justifyContent: 'flex-start',
    textAlign: 'left',
  },
  deleteButton: {
    '&:hover': {
      color: Colors.red,
    },
    marginLeft: -20,
  },
  noDataCell: {
    padding: '10px 20px',
  },
  footer: {
    height: 60,
    display: 'flex',
    justifyContent: 'space-between',
    paddingRight: 14,

    [theme.breakpoints.up('xl')]: {
      '& .MuiFlatPageButton-sizeSmallCurrent, .MuiFlatPageButton-sizeSmallStandard': {
        paddingLeft: 10,
        paddingRight: 10,
      },
      '& .MuiFlatPageButton-sizeSmall': {
        minWidth: 10,
      },
    },
    [theme.breakpoints.down('sm')]: {
      '& .MuiFlatPageButton-sizeSmallCurrent, .MuiFlatPageButton-sizeSmallStandard': {
        paddingLeft: 5,
        paddingRight: 5,
      },
      '& .MuiFlatPageButton-sizeSmall': {
        minWidth: 5,
      },
    },
    '& .MuiFlatPageButton-rootEllipsis.MuiButton-root.Mui-disabled': {
      color: Colors.lightGrey,
    },
  },
  showingCount: {
    padding: '8px 8px 8px 20px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    fontSize: 12,
    color: Colors.lightGrey,
    textAlign: 'left',
  },
  lightThemeGrey: {
    color: Colors.disabledGrey,
  },
  showingCountSmall: {
    flexDirection: 'column',
    justifyContent: 'center',
  },
  showingCountContent: {
    display: 'inline-block',
    marginRight: 5,
  },
  noSortLabel: {
    color: Colors.lightGrey,
    padding: '11px 20px',
  },
  checkboxHeader: {
    color: Colors.lightGrey,
    textAlign: 'center',
  },
  checkboxCell: {
    textAlign: 'center',
  },

  cellPadding: {
    paddingLeft: 10,
  },
  datePicker: {
    marginLeft: 10,
    marginRight: 5,
  },
  timePickerError: {
    '& .rc-time-picker-input': {
      color: Colors.errorRed,
      borderColor: Colors.errorRed,
    },
    '& .rc-time-picker-clear-icon:after': {
      color: Colors.errorRed,
    },
  },
  warningCell: {
    borderLeft: `8px solid ${Colors.warningYellow}`,
    cursor: 'default',
  },
  errorCell: {
    borderLeft: `8px solid ${Colors.red}`,
    cursor: 'default',
  },
  warningErrorIcon: {
    margin: 7,
  },
}));

const AjaxAccessLevelTable = (props) => {
  const {
    page,
    ajaxColumns,
    data,
    itemsPerPage,
    totalElements,
    handleClick,
    handleEditBlur,
    size,
    lightTheme,
    activeRowValue,
    activeRowField,
    checkboxColumn,
    deleteColumn,
    addWeeklyColumn,
    deleteWeeklyColumn,
    noHeader,
    noFooter,
    enableCapabilityFilter,
  } = props;

  const { t } = useTranslation();
  const classes = useStyles();

  //TODO: Only disables the selection. Graphically could be shown that the row is disabled and give error text when pressed. "Element cannot be selected"
  const handleEnabledClick = (row) => {
    if (!enableCapabilityFilter || row.capability) {
      handleClick(row);
    }
  };

  const createSortHandler = (property) => (event) => {
    handleRequestSort(event, property);
  };
  const handleRequestSort = (event, property) => {
    const isAsc = props.sortBy === property && props.sortDirection === 'ASC';
    const direction = isAsc ? 'DESC' : 'ASC';
    if (props.handleSort) {
      props.handleSort(property, direction);
    }
  };

  const handleChangePage = (event, offset, page) => {
    props.changePage(page - 1);
  };
  const isSmall = props.size.width < Metrics.tableWidth.small;
  const isTiny = props.size.width < Metrics.tableWidth.tiny;

  return (
    <div className={clsx(classes.table, lightTheme && classes.lightTheme)}>
      {!noHeader && (
        <div className={classes.headerContainer}>
          <TableHeader size={size} {...props} />
        </div>
      )}
      <div className={classes.tableWrapper}>
        <Table className={classes.table} aria-label="custom pagination table">
          <TableHead>
            <TableRow>
              {(props.hasWarningsOrErrors || checkboxColumn === 'start') && (
                <TableCell className={classes.emptyHeaderCell} key={'checkbox-start-Header'} />
              )}
              {ajaxColumns.map((column, index) => {
                return (
                  <TableCell
                    key={column.field}
                    sortDirection={
                      props.sortDirection && props.sortBy === column.field ? props.sortDirection.toLowerCase() : false
                    }
                    {...(deleteColumn && index === ajaxColumns.length - 1 && { colSpan: 1 })}
                  >
                    {!props.handleSort || (props.noSort && props.noSort.includes(column.field)) ? (
                      <div className={classes.noSortLabel}>{column.title}</div>
                    ) : (
                      <TableSortLabel
                        className={classes.tableCellButton}
                        active={props.sortBy === column.field}
                        direction={props.sortDirection ? props.sortDirection.toLowerCase() : 'asc'}
                        onClick={createSortHandler(column.field)}
                        hideSortIcon={false}
                        IconComponent={SortIcon}
                      >
                        {column.title}
                      </TableSortLabel>
                    )}
                  </TableCell>
                );
              })}
              {checkboxColumn === 'end' && (
                <TableCell key={'checkbox-end-Header'}>
                  <div className={classes.checkboxHeader}>{props.checkboxColumnTitle}</div>
                </TableCell>
              )}
              {deleteColumn && <TableCell className={classes.emptyHeaderCell} key={'delete-Header'} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {typeof data !== 'undefined' && data && data.length > 0 ? (
              data.map((row, index) => {
                const labelId = `table-checkbox-${index}`;
                const isItemSelected =
                  checkboxColumn &&
                  props.checkedArray.findIndex((item) => item[activeRowField] === row[activeRowField]) !== -1;
                const isWarningRow = props.colorRowField && row[props.colorRowField].includes(props.warningCondition);
                const isErrorRow = props.colorRowField && row[props.colorRowField].includes(props.errorCondition);

                return (
                  <TableRow
                    key={index}
                    // following property makes the line appear 'selected' when the user has clicked on it
                    className={clsx(
                      activeRowField && activeRowValue && row[activeRowField] === activeRowValue && classes.activeRow
                    )}
                  >
                    {props.hasWarningsOrErrors && (
                      <TableCell
                        className={clsx(isWarningRow && classes.warningCell, isErrorRow && classes.errorCell)}
                        key={'warningErrorCellStart' + index}
                      >
                        {isWarningRow && (
                          <img src={TableIconWarning} alt="warning" className={classes.warningErrorIcon} />
                        )}
                        {isErrorRow && <img src={TableIconError} alt="error" className={classes.warningErrorIcon} />}
                      </TableCell>
                    )}
                    {checkboxColumn === 'start' && (
                      <TableCell key={'checkboxCellStart' + index}>
                        <Checkbox
                          checked={isItemSelected}
                          inputProps={{ 'aria-labelledby': labelId }}
                          onChange={(event) => handleEnabledClick(row)}
                        />
                      </TableCell>
                    )}
                    {ajaxColumns.map((column) => {
                      return (
                        <TableCell
                          key={column.field + index}
                          className={clsx(props.cellPadding && classes.cellPadding)}
                        >
                          {column.type === 'textInput' && (
                            <InputBase
                              className={classes.editableInput}
                              value={row[activeRowField] === activeRowValue ? props.editableValue : row[column.field]}
                              name={props.editableColumn}
                              onChange={props.handleEditColumn}
                              {...(handleClick && { onClick: (event) => handleEnabledClick(row) })}
                              {...(handleEditBlur && { onBlur: handleEditBlur })}
                            />
                          )}
                          {column.type === 'dateInput' && (
                            <DatePicker
                              className={classes.datePicker}
                              showPopperArrow={false}
                              selected={row[column.field] ? new Date(row[column.field]) : new Date()}
                              onChange={(date) => props.handleChangeDate(date, row, column.field)}
                              locale={i18n.language}
                              dateFormat="d.M.yyyy"
                              customInput={<input className="rc-time-picker-input" />}
                            />
                          )}
                          {column.type === 'timeInput' && (
                            <TimePicker
                              className={clsx(props.timeErrorRowIndexes.includes(index) && classes.timePickerError)}
                              showSecond={false}
                              value={row[column.field] ? moment.utc(row[column.field], 'HH:mm:ss') : null}
                              onChange={(time) => props.handleChangeTime(time, row, column.field)}
                              disabled={props.disableTimeInputIfChecked && isItemSelected}
                            />
                          )}
                          {props.copyable && !column.type && (
                            <div
                              className={clsx(
                                classes.tableCellButton,
                                classes.tableBodyCellButton,
                                classes.defaultCursor
                              )}
                            >
                              {row[column.field]}
                            </div>
                          )}
                          {column.type === 'specialHTML' && (
                            <ButtonBase
                              disabled={!handleClick || column.type === 'disabled'}
                              className={clsx(classes.tableCellButton, classes.tableBodyCellButton)}
                              {...(handleClick && { onClick: (event) => handleEnabledClick(row) })}
                              centerRipple
                            >
                              <div dangerouslySetInnerHTML={{ __html: row[column.field] }} />
                            </ButtonBase>
                          )}
                          {!props.copyable && (!column.type || column.type === 'disabled') && (
                            <ButtonBase
                              disabled={!handleClick || column.type === 'disabled'}
                              className={clsx(classes.tableCellButton, classes.tableBodyCellButton)}
                              {...(handleClick && { onClick: (event) => handleEnabledClick(row) })}
                              centerRipple
                            >
                              {row[column.field]}
                            </ButtonBase>
                          )}
                        </TableCell>
                      );
                    })}
                    {checkboxColumn === 'end' && (
                      <TableCell key={'checkboxCell' + index} className={classes.checkboxCell}>
                        <Checkbox
                          checked={isItemSelected}
                          inputProps={{ 'aria-labelledby': labelId }}
                          onChange={(event) => handleEnabledClick(row)}
                        />
                      </TableCell>
                    )}
                    {deleteColumn && (
                      <TableCell className={classes.deleteCell} key={'deleteCell' + index}>
                        <IconButton
                          className={classes.deleteButton}
                          key={'deleteButton' + index}
                          aria-label="delete tag"
                          color="inherit"
                          onClick={() => props.handleDeleteButtonClick(row)}
                        >
                          <ClearIcon className={classes.icon} />
                        </IconButton>
                      </TableCell>
                    )}
                    {deleteWeeklyColumn && !row.mandatory && (
                      //copy of above with different conditions
                      <TableCell className={classes.deleteCell} key={'deleteCell' + index}>
                        <IconButton
                          className={classes.deleteButton}
                          key={'deleteButton' + index}
                          aria-label="delete tag"
                          color="inherit"
                          onClick={() => props.handleDeleteButtonClick(row)}
                        >
                          <ClearIcon className={classes.icon} />
                        </IconButton>
                      </TableCell>
                    )}
                    {addWeeklyColumn && row.mandatory && (
                      <TableCell className={classes.deleteCell} key={'addCell' + index}>
                        <IconButton
                          className={classes.addButton}
                          key={'addButton' + index}
                          aria-label="add tag"
                          color="inherit"
                          onClick={() => props.handleAddButtonClick(row)}
                        >
                          <AddIcon className={classes.icon} />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  className={classes.noDataCell}
                  colSpan={
                    props.deleteColumn && checkboxColumn
                      ? ajaxColumns.length + 2
                      : props.deleteColumn && !checkboxColumn
                      ? ajaxColumns.length + 1
                      : ajaxColumns.length
                  }
                >
                  {t('accessLevels.accessLevelNoHubs')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {!noFooter && (
        <div className={classes.footer}>
          <div
            className={clsx([
              classes.showingCount,
              isSmall && classes.showingCountSmall,
              props.lightTheme && classes.lightThemeGrey,
            ])}
            style={isTiny ? { maxWidth: 90 } : null}
          >
            <span className={classes.showingCountContent}>{`${t('table.showing')} `}</span>
            <span className={classes.showingCountContent}>
              {`${totalElements === 0 ? 0 : page * itemsPerPage + 1}-${Math.min(
                totalElements,
                (page + 1) * itemsPerPage
              )}`}
              &nbsp;/&nbsp;{totalElements}
            </span>
          </div>
          <Pagination
            previousPageLabel={<KeyboardArrowLeft />}
            nextPageLabel={<KeyboardArrowRight />}
            innerButtonCount={2}
            limit={itemsPerPage}
            offset={page * itemsPerPage}
            total={totalElements}
            onClick={handleChangePage}
            otherPageColor={'inherit'}
            currentPageColor={'secondary'}
            size={'small'}
          />
        </div>
      )}
    </div>
  );
};

export default withSize()(AjaxAccessLevelTable);
